// Mots du glossaire dans le texte des cours.
// - Détection automatique : la première apparition de chaque mot du glossaire devient un lien vers sa définition.
//   Elle est faite à l'affichage : une définition ajoutée plus tard est reconnue dans les anciens cours.
// - Exceptions par cours : glossary_off (mots à ne pas souligner) et liens posés à la main dans l'éditeur
//   (<a data-glossary="id">), qui sont toujours conservés.
import { safeHtml } from "./sanitize";
/** Mot cherché dans le texte : le titre de la définition sans la partie entre parenthèses (« DCA (…) » → « DCA »). */
export const glossaryWord = (title) => (title || "").replace(/\s*\([^)]*\)\s*/g, " ").trim();
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Expression qui trouve le mot (et son pluriel en s ou x), sans le confondre avec un morceau d'un autre mot. */
export const termRegex = (word, flags = "iu") => new RegExp(`(?<![\\p{L}\\p{N}])${escapeRe(word)}(?:s|x)?(?![\\p{L}\\p{N}])`, flags);
// Jamais de lien dans les titres, les liens existants, le code, les boutons, les formules ni la coupure d'aperçu
const SKIP = new Set(["A", "H1", "H2", "H3", "H4", "H5", "H6", "CODE", "PRE", "BUTTON", "SCRIPT", "STYLE", "FIGCAPTION"]);
const isSkipped = (node, root) => {
  for (let el = node.parentElement; el && el !== root; el = el.parentElement) {
    if (SKIP.has(el.tagName) || el.hasAttribute("data-no-gloss") || el.hasAttribute("data-preview-break")) return true;
    if (/^(block|inline)-math$/.test(el.getAttribute("data-type") || "") || el.classList.contains("katex")) return true; // formules
  }
  return false;
};
/**
 * Transforme en liens les mots du glossaire trouvés dans `root` (élément DOM).
 * terms : [{ id, slug, title, description }] ; off : ids à ne pas souligner ; skipId : la définition affichée elle-même.
 * Renvoie { auto: [ids liés automatiquement], manual: [ids liés à la main] }.
 */
export function linkGlossary(root, terms = [], { off = [], skipId = null, hrefFor = (t) => `/cours/${t.slug}` } = {}) {
  const result = { auto: [], manual: [] };
  if (!root || !terms.length) return result;
  const byId = Object.fromEntries(terms.map((t) => [t.id, t]));
  const used = new Set();
  // 1) Liens posés à la main dans l'éditeur : on met à jour l'adresse et la définition au survol
  root.querySelectorAll("a[data-glossary]").forEach((a) => {
    const t = byId[a.getAttribute("data-glossary")];
    if (!t) return;
    a.classList.add("gloss");
    a.setAttribute("href", hrefFor(t));
    a.setAttribute("data-gloss-def", t.description || "");
    a.setAttribute("data-gloss-title", t.title || "");
    used.add(t.id);
    result.manual.push(t.id);
  });
  // 2) Détection automatique : les mots les plus longs d'abord (« Taux directeur » avant « Taux »)
  const offSet = new Set(off);
  const candidates = terms
    .filter((t) => t.id !== skipId && !used.has(t.id))
    .map((t) => ({ t, word: glossaryWord(t.title) }))
    .filter((x) => x.word.length >= 2)
    .sort((a, b) => b.word.length - a.word.length);
  for (const { t, word } of candidates) {
    const re = termRegex(word);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (isSkipped(node, root)) continue;
      const m = re.exec(node.data);
      if (!m) continue;
      result.auto.push(t.id);
      if (!offSet.has(t.id)) {
        const match = node.splitText(m.index);
        match.splitText(m[0].length);
        const a = document.createElement("a");
        a.className = "gloss";
        a.href = hrefFor(t);
        a.setAttribute("data-glossary", t.id);
        a.setAttribute("data-gloss-auto", "");
        a.setAttribute("data-gloss-def", t.description || "");
        a.setAttribute("data-gloss-title", t.title || "");
        a.textContent = match.data;
        match.replaceWith(a);
      }
      break; // seulement la première apparition
    }
  }
  return result;
}
/** Pour l'éditeur : quels mots du glossaire sont présents dans ce HTML (sans le modifier). */
export function detectGlossary(html, terms = [], skipId = null) {
  if (typeof document === "undefined" || !html) return { auto: [], manual: [] };
  const box = document.createElement("div");
  box.innerHTML = safeHtml(html);
  return linkGlossary(box, terms, { skipId });
}
